import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeRange } from '@/core/DateTimeRange.molecule';
import { sqWorksheetStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { isValidCapsule } from '@/tools/manualCondition/conditionFormula.service';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { RangeField } from '@/core/DateTimeRange.molecule';
import type { TrackCategory } from '@/track/track.constants';
import type { Capsule } from '@/utilities/datetime.constants';

interface CapsuleInputIF {
  setCapsuleInputValidStatus?: (isValid: boolean) => void;
  onChange?: (capsuleWindow: Capsule) => void;
  trackInformation?: string;
  trackAction: string;
  trackCategory: TrackCategory;
  dateFormat?: string;
  capsuleWindow?: Capsule;
  autoUpdateDate?: boolean;
}

export const CapsuleInput = (props: CapsuleInputIF) => {
  const {
    setCapsuleInputValidStatus,
    capsuleWindow = {
      startTime: moment().subtract(1, 'hour').valueOf(),
      endTime: moment().valueOf(),
      properties: [],
    },
    onChange,
    trackInformation,
    trackAction,
    trackCategory,
    dateFormat,
    autoUpdateDate = true,
  } = props;

  const { timezone } = useFlux(sqWorksheetStore);

  const { t } = useTranslation();

  useEffect(() => {
    setCapsuleWindowStatus(isValidCapsule(capsuleWindow));
  }, []);

  const setCapsuleWindow = (capsuleWindow: RangeField<moment.Moment>) => {
    const capsule = {
      ...capsuleWindow,
      startTime: capsuleWindow.start.valueOf(),
      endTime: capsuleWindow.end.valueOf(),
    };
    _.isFunction(onChange) && onChange(capsule);
    setCapsuleWindowStatus(isValidCapsule(capsule));
  };

  const setCapsuleWindowStatus = (isValid: boolean) => {
    _.isFunction(setCapsuleInputValidStatus) && setCapsuleInputValidStatus(isValid);
  };

  return (
    <div {...stylex.props(styles.editor)}>
      <div {...stylex.props(styles.headings)}>
        <span {...stylex.props(styles.heading)}>{t('CAPSULE_INPUT_DATE_TIME_LABEL')}</span>
        <span {...stylex.props(styles.heading)}>{t('CAPSULE_INPUT_DATE_TIME_LABEL')}</span>
      </div>
      <div {...stylex.props(styles.dateRange)}>
        <DateTimeRange
          autoUpdateDate={autoUpdateDate}
          startTime={moment(capsuleWindow.startTime)}
          endTime={moment(capsuleWindow.endTime)}
          onChange={setCapsuleWindow}
          dateFormat={dateFormat}
          timezone={timezone}
          hideTimezone={true}
          trackCategory={trackCategory}
          trackAction={trackAction}
          trackInformation={{
            start: `Start. ${trackInformation}`,
            end: `End. ${trackInformation}`,
          }}
        />
      </div>
      {capsuleWindow && !isValidCapsule(capsuleWindow) && (
        <p {...stylex.props(styles.validation)}>{t('FORM.START_BEFORE_END')}</p>
      )}
    </div>
  );
};

const styles = stylex.create({
  editor: { display: 'flex', flexDirection: 'column' },
  headings: { display: 'flex', flexDirection: 'row', marginBottom: '0.3125rem', justifyContent: 'space-around' },
  heading: { fontWeight: 700 },
  dateRange: {
    display: 'flex',
    flexDirection: 'row',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  validation: { color: colors.borderInvalid, textAlign: 'center' },
});
